import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/errorUtils';
import '../Subscription/SubscriptionBanner.css';
import './Navbar.css';

function Navbar() {
  const { user, logout, resendVerification } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const userMenuRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleResendVerification = async () => {
    if (isResendingVerification) return;
    setIsResendingVerification(true);
    const result = await resendVerification();
    if (result.success) {
      showToast(result.message || 'Verification email sent successfully', 'success');
    } else {
      showToast(result.error || 'Failed to resend verification email', 'error');
    }
    setIsResendingVerification(false);
  };

  const handleOpenCustomerPortal = async () => {
    if (openingPortal) return;
    try {
      setOpeningPortal(true);
      const response = await api.post('/subscription/customer-portal');
      if (response.data?.url) {
        window.location.href = response.data.url;
        return;
      }
      showToast('Failed to open customer portal', 'error');
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to open customer portal'), 'error');
    } finally {
      setOpeningPortal(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    api.get('/subscription/status', { signal: controller.signal })
      .then(response => setSubscriptionStatus(response.data))
      .catch(error => {
        if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
        // Status is optional; avoid blocking the UI on failures
      });
    return () => controller.abort();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  return (
    <>
      <nav className="navbar">
        <div className="navbar-content">
          <div className="navbar-left">
            <Link to="/" className="navbar-brand" style={{ textDecoration: 'none', color: 'white', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img src="/logo.svg" alt="Options Tracker Logo" style={{ width: '32px', height: '32px' }} />
              <span>Options Tracker</span>
            </Link>
            <div className="navbar-links">
              <Link to="/dashboard">Dashboard</Link>
              <Link to="/positions">Positions</Link>
              <Link to="/calendar">Calendar</Link>
              <Link to="/trades">Trades</Link>
              <Link to="/accounts">Accounts</Link>
              <Link to="/tools">Tools</Link>
              <Link to="/statistics">Statistics</Link>
            </div>
            <button 
              className="mobile-menu-toggle" 
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
          <div className="navbar-right">
            {subscriptionStatus?.is_free_user && (
              <Link
                to="/profile?tab=subscription"
                className="plan-badge free"
                title="Free plan - Upgrade to Pro for unlimited access"
              >
                Free
              </Link>
            )}
            {subscriptionStatus?.is_pro_user && (
              <Link
                to="/profile?tab=subscription"
                className="plan-badge pro"
                title="Pro plan"
              >
                Pro
              </Link>
            )}
            <Link to="/profile" className="settings-link" title="Profile & Settings">
              <span className="settings-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 6h10M4 18h10M14 12h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  <circle cx="16" cy="6" r="2" stroke="currentColor" strokeWidth="1.8"/>
                  <circle cx="10" cy="12" r="2" stroke="currentColor" strokeWidth="1.8"/>
                  <circle cx="16" cy="18" r="2" stroke="currentColor" strokeWidth="1.8"/>
                </svg>
              </span>
              Settings
            </Link>
            <div className="user-menu" ref={userMenuRef}>
              <button type="button" className="user-menu-trigger" onClick={toggleUserMenu} aria-haspopup="true" aria-expanded={userMenuOpen}>
                <span className="user-menu-name">
                  {user?.first_name && user?.last_name 
                    ? `${user.first_name} ${user.last_name}` 
                    : user?.email}
                </span>
                <span className={`user-menu-caret ${userMenuOpen ? 'open' : ''}`} aria-hidden="true">▾</span>
              </button>
              {userMenuOpen && (
                <div className="user-menu-dropdown">
                  <div className="user-menu-header">
                    {user?.first_name && user?.last_name 
                      ? `${user.first_name} ${user.last_name}` 
                      : user?.email}
                  </div>
                  <button type="button" className="user-menu-item" onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {user && user.email_verified === false && (
        <div className="subscription-banner warning">
          <div className="subscription-banner-content">
            <div className="subscription-banner-text">
              <strong>Email verification required.</strong>
              <span>Verify your email to access all features and create or edit data.</span>
            </div>
            <div className="subscription-banner-actions">
              <button
                type="button"
                className="btn btn-primary btn-small"
                onClick={handleResendVerification}
                disabled={isResendingVerification}
              >
                {isResendingVerification ? 'Sending...' : 'Resend verification'}
              </button>
              <Link to="/profile" className="btn btn-ghost btn-small">
                Update email
              </Link>
            </div>
          </div>
        </div>
      )}

      {user && subscriptionStatus?.subscription_status === 'past_due' && (
        <div className="subscription-banner urgent">
          <div className="subscription-banner-content">
            <div className="subscription-banner-text">
              <strong>Payment failed — update your card to restore access.</strong>
              <span>You’re in view-only mode until your payment method is updated.</span>
            </div>
            <div className="subscription-banner-actions">
              <button
                type="button"
                className="btn btn-primary btn-small"
                onClick={handleOpenCustomerPortal}
                disabled={openingPortal}
              >
                {openingPortal ? 'Opening...' : 'Update Billing'}
              </button>
              <Link to="/profile?tab=subscription" className="btn btn-ghost btn-small">
                View Details
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* View-only banner removed - handled by SubscriptionBanner component */}
      
      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>
      
      {/* Mobile Sidebar Menu */}
      <div className={`mobile-menu-sidebar ${mobileMenuOpen ? 'active' : ''}`}>
        <div className="mobile-menu-header">
          <Link to="/" className="navbar-brand" style={{ textDecoration: 'none', color: 'white', display: 'flex', alignItems: 'center', gap: '10px' }} onClick={closeMobileMenu}>
            <img src="/logo.svg" alt="Options Tracker Logo" style={{ width: '28px', height: '28px' }} />
            <span>Options Tracker</span>
          </Link>
          <button className="mobile-close-btn" onClick={closeMobileMenu} aria-label="Close menu">
            ×
          </button>
        </div>
        <div className="mobile-menu-content">
          <div className="navbar-links">
            <Link to="/dashboard" onClick={closeMobileMenu}>Dashboard</Link>
            <Link to="/positions" onClick={closeMobileMenu}>Positions</Link>
            <Link to="/calendar" onClick={closeMobileMenu}>Calendar</Link>
            <Link to="/trades" onClick={closeMobileMenu}>Trades</Link>
            <Link to="/accounts" onClick={closeMobileMenu}>Accounts</Link>
            <Link to="/tools" onClick={closeMobileMenu}>Tools</Link>
            <Link to="/statistics" onClick={closeMobileMenu}>Statistics</Link>
          </div>
          <div className="mobile-menu-divider"></div>
          <div className="navbar-links navbar-links-secondary">
            <Link to="/blog" onClick={closeMobileMenu}>Blog</Link>
            <Link to="/pricing" onClick={closeMobileMenu}>Pricing</Link>
            <Link to="/how-to-use" onClick={closeMobileMenu}>How to Use</Link>
          </div>
        </div>
        <div className="mobile-menu-footer">
          {subscriptionStatus?.is_free_user && (
            <Link
              to="/profile?tab=subscription"
              className="plan-badge free"
              onClick={closeMobileMenu}
              title="Free plan - Upgrade to Pro"
            >
              Free
            </Link>
          )}
          {subscriptionStatus?.is_pro_user && (
            <Link
              to="/profile?tab=subscription"
              className="plan-badge pro"
              onClick={closeMobileMenu}
              title="Pro plan"
            >
              Pro
            </Link>
          )}
          <Link to="/profile" className="settings-link" onClick={closeMobileMenu}>
            <span className="settings-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M4 6h10M4 18h10M14 12h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                <circle cx="16" cy="6" r="2" stroke="currentColor" strokeWidth="1.8"/>
                <circle cx="10" cy="12" r="2" stroke="currentColor" strokeWidth="1.8"/>
                <circle cx="16" cy="18" r="2" stroke="currentColor" strokeWidth="1.8"/>
              </svg>
            </span>
            Settings
          </Link>
          <span className="profile-label">
            {user?.first_name && user?.last_name 
              ? `${user.first_name} ${user.last_name}` 
              : user?.email}
          </span>
          <button className="btn btn-secondary" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>
    </>
  );
}

export default Navbar;

